import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { AuthShell } from "@/components/auth/AuthShell";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/Field";
import { useSession } from "@/context/SessionContext";
import { ApiRequestError } from "@/lib/api/client";
import { useT } from "@/lib/i18n";
import { describeError } from "@/lib/i18n/errors";

const MIN_PASSWORD = 10;

export function RegisterPage() {
  const { register } = useSession();
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/dashboard";

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const tooShort = password !== "" && password.length < MIN_PASSWORD;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (tooShort) return;
    setBusy(true);
    setProblem(null);
    setFields({});
    try {
      await register({ email: email.trim(), password, displayName: displayName.trim() });
      void navigate(next, { replace: true });
    } catch (error) {
      if (error instanceof ApiRequestError) setFields(error.fields);
      setProblem(describeError(error, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      footer={
        <>
          <Seo
            description="Create a free BS Meet account to host video meetings with screen sharing, slides and a shared whiteboard. Joining a meeting never needs one."
            image="/screenshots/register-wide.png"
            imageAlt="The BS Meet create-account page"
            path="/register"
            title={t("auth.createTitle")}
          />
          {t("auth.haveAccount")}{" "}
          <Link
            className="font-medium text-brand hover:underline"
            to={`/login?next=${encodeURIComponent(next)}`}
          >
            {t("nav.signIn")}
          </Link>
        </>
      }
      subtitle={t("auth.createSubtitle")}
      title={t("auth.createTitle")}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <TextField
          autoComplete="name"
          error={fields["displayName"]}
          label={t("auth.name")}
          maxLength={60}
          onChange={(event) => {
            setDisplayName(event.target.value);
          }}
          required
          value={displayName}
        />
        <TextField
          autoComplete="email"
          error={fields["email"]}
          label={t("auth.email")}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          required
          type="email"
          value={email}
        />
        <TextField
          autoComplete="new-password"
          error={
            fields["password"] ?? (tooShort ? `At least ${MIN_PASSWORD} characters` : undefined)
          }
          hint={t("auth.passwordHint", { count: MIN_PASSWORD })}
          label={t("auth.password")}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          required
          type="password"
          value={password}
        />

        {problem !== null ? (
          <p className="text-sm text-danger" role="alert">
            {problem}
          </p>
        ) : null}

        <Button loading={busy} size="lg" type="submit">
          {t("nav.createAccount")}
        </Button>
      </form>
    </AuthShell>
  );
}
